import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { PersonaLineageNode } from '../../types/crossSystem';

interface LineageCanvasProps {
  rootNodes: PersonaLineageNode[];
  focusPersonaId?: string;
  scope: 'full' | 'focused';
  onNavigate: (personaId: string) => void;
}

const NODE_WIDTH = 220;
const NODE_HEIGHT = 80;
const H_GAP = 100;
const V_GAP = 20;

// Deterministic color from name (matches PersonaBadge palette)
const PALETTE = [
  '#7c3aed', '#2563eb', '#0891b2', '#059669',
  '#d97706', '#dc2626', '#db2777', '#9333ea',
  '#4f46e5', '#0d9488', '#ca8a04', '#6366f1',
];

function nameToColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

function getInitials(name: string): string {
  return name
    .split(/[_\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();
}

interface LayoutNode {
  id: string;
  name: string;
  x: number;
  y: number;
  revision: number;
  remix_point?: string;
  tags: string[];
  capabilities: Record<string, any>;
  children: LayoutNode[];
  depth: number;
}

function layoutTree(nodes: PersonaLineageNode[], startX = 0, startY = 0): LayoutNode[] {
  let yOffset = startY;

  function layout(node: PersonaLineageNode, depth: number): LayoutNode {
    const x = startX + depth * (NODE_WIDTH + H_GAP);
    const children = node.children.map(c => layout(c, depth + 1));

    let y: number;
    if (children.length === 0) {
      y = yOffset;
      yOffset += NODE_HEIGHT + V_GAP;
    } else {
      const first = children[0].y;
      const last = children[children.length - 1].y;
      y = (first + last) / 2;
    }

    return {
      id: node.persona_id,
      name: node.name,
      x, y,
      revision: node.revision,
      remix_point: node.remix_point,
      tags: node.tags,
      capabilities: node.capability_summary || {},
      children,
      depth,
    };
  }

  return nodes.map(n => layout(n, 0));
}

function flattenNodes(nodes: LayoutNode[]): LayoutNode[] {
  const flat: LayoutNode[] = [];
  function walk(n: LayoutNode) {
    flat.push(n);
    n.children.forEach(walk);
  }
  nodes.forEach(walk);
  return flat;
}

const LineageCanvas: React.FC<LineageCanvasProps> = ({ rootNodes, focusPersonaId, scope, onNavigate }) => {
  const { darkMode } = useTheme();
  const svgRef = useRef<SVGSVGElement>(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0, tx: 0, ty: 0 });

  const layoutNodes = layoutTree(rootNodes);
  const allNodes = flattenNodes(layoutNodes);

  // Auto-center on focus persona
  useEffect(() => {
    if (focusPersonaId && svgRef.current) {
      const target = allNodes.find(n => n.id === focusPersonaId);
      if (target) {
        const rect = svgRef.current.getBoundingClientRect();
        setTransform({
          x: rect.width / 2 - target.x - NODE_WIDTH / 2,
          y: rect.height / 2 - target.y - NODE_HEIGHT / 2,
          scale: 1,
        });
      }
    }
  }, [focusPersonaId, rootNodes]);

  // Pan handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    panStart.current = { x: e.clientX, y: e.clientY, tx: transform.x, ty: transform.y };
  }, [transform]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning) return;
    setTransform(prev => ({
      ...prev,
      x: panStart.current.tx + (e.clientX - panStart.current.x),
      y: panStart.current.ty + (e.clientY - panStart.current.y),
    }));
  }, [isPanning]);

  const handleMouseUp = useCallback(() => setIsPanning(false), []);

  // Zoom handler
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setTransform(prev => ({
      ...prev,
      scale: Math.min(2, Math.max(0.3, prev.scale * delta)),
    }));
  }, []);

  const lineColor = darkMode ? '#4a4a6a' : '#c0c0c0';
  const focusColor = '#7c3aed';

  function renderEdges(nodes: LayoutNode[]): React.ReactNode[] {
    const edges: React.ReactNode[] = [];
    function walk(n: LayoutNode) {
      for (const child of n.children) {
        const sx = n.x + NODE_WIDTH;
        const sy = n.y + NODE_HEIGHT / 2;
        const ex = child.x;
        const ey = child.y + NODE_HEIGHT / 2;
        const mx = (sx + ex) / 2;
        const isActiveEdge = n.id === focusPersonaId || child.id === focusPersonaId;
        edges.push(
          <path
            key={`${n.id}-${child.id}`}
            d={`M${sx},${sy} C${mx},${sy} ${mx},${ey} ${ex},${ey}`}
            stroke={isActiveEdge ? focusColor : lineColor}
            strokeWidth={isActiveEdge ? 2.5 : 1.5}
            strokeDasharray={isActiveEdge ? 'none' : '6 4'}
            fill="none"
            opacity={isActiveEdge ? 1 : 0.5}
          />
        );
        walk(child);
      }
    }
    nodes.forEach(walk);
    return edges;
  }

  function renderNodes(): React.ReactNode[] {
    return allNodes.map(n => {
      const isFocus = n.id === focusPersonaId;
      const isHovered = n.id === hoveredId;
      const color = nameToColor(n.name);
      const initials = getInitials(n.name);
      const capCount = Object.keys(n.capabilities).length;
      const displayName = n.name.replace(/_/g, ' ');
      const truncName = displayName.length > 22 ? displayName.slice(0, 22) + '...' : displayName;

      const remixAge = n.remix_point
        ? Math.floor((Date.now() - new Date(n.remix_point).getTime()) / 86400000)
        : null;

      // Node background
      const bgFill = darkMode ? '#1e1e36' : '#ffffff';
      const hoverFill = darkMode ? '#2a2a4a' : '#f5f3ff';

      return (
        <g
          key={n.id}
          transform={`translate(${n.x}, ${n.y})`}
          onClick={(e) => { e.stopPropagation(); onNavigate(n.id); }}
          onMouseEnter={() => setHoveredId(n.id)}
          onMouseLeave={() => setHoveredId(null)}
          style={{ cursor: 'pointer' }}
        >
          {/* Glow for focus node */}
          {isFocus && (
            <rect
              x={-4} y={-4}
              width={NODE_WIDTH + 8} height={NODE_HEIGHT + 8}
              rx={14}
              fill="none"
              stroke={focusColor}
              strokeWidth={1}
              opacity={0.3}
              filter="url(#glow)"
            />
          )}

          {/* Card background */}
          <rect
            width={NODE_WIDTH} height={NODE_HEIGHT}
            rx={10}
            fill={isHovered ? hoverFill : bgFill}
            stroke={isFocus ? focusColor : isHovered ? color : (darkMode ? '#3a3a5c' : '#e0e0e0')}
            strokeWidth={isFocus ? 2.5 : 1}
          />

          {/* Left accent bar */}
          <rect
            x={0} y={0}
            width={4} height={NODE_HEIGHT}
            rx={2}
            fill={color}
            opacity={isFocus ? 1 : 0.6}
          />

          {/* Avatar circle */}
          <circle
            cx={24} cy={NODE_HEIGHT / 2}
            r={14}
            fill={color}
            opacity={isFocus ? 1 : 0.8}
          />
          <text
            x={24} y={NODE_HEIGHT / 2 + 4}
            textAnchor="middle"
            fill="#fff"
            fontSize={10}
            fontWeight={700}
          >
            {initials}
          </text>

          {/* Name */}
          <text
            x={46} y={24}
            fill={isFocus ? (darkMode ? '#e8e0ff' : '#5b21b6') : (darkMode ? '#e0e0e0' : '#1a1a1a')}
            fontSize={13}
            fontWeight={isFocus ? 700 : 600}
          >
            {truncName}
          </text>

          {/* Metadata line */}
          <text
            x={46} y={42}
            fill={darkMode ? '#888' : '#888'}
            fontSize={10}
          >
            rev {n.revision}
            {remixAge != null ? ` · ${remixAge === 0 ? 'today' : remixAge + 'd ago'}` : ''}
            {capCount > 0 ? ` · ${capCount} caps` : ''}
          </text>

          {/* Tags */}
          {n.tags.length > 0 && (
            <g transform={`translate(46, 52)`}>
              {n.tags.slice(0, 3).map((tag, i) => {
                const tagX = i * 52;
                return (
                  <g key={tag} transform={`translate(${tagX}, 0)`}>
                    <rect
                      width={48} height={16} rx={4}
                      fill={darkMode ? 'rgba(124,58,237,0.15)' : 'rgba(124,58,237,0.1)'}
                    />
                    <text
                      x={24} y={11}
                      textAnchor="middle"
                      fill={darkMode ? '#a78bfa' : '#7c3aed'}
                      fontSize={8}
                    >
                      {tag.length > 7 ? tag.slice(0, 7) + '..' : tag}
                    </text>
                  </g>
                );
              })}
              {n.tags.length > 3 && (
                <text
                  x={3 * 52 + 4} y={11}
                  fill={darkMode ? '#666' : '#999'}
                  fontSize={8}
                >
                  +{n.tags.length - 3}
                </text>
              )}
            </g>
          )}

          {/* Focus badge */}
          {isFocus && (
            <>
              <rect
                x={NODE_WIDTH - 40} y={4}
                width={36} height={16} rx={8}
                fill={focusColor}
              />
              <text
                x={NODE_WIDTH - 22} y={15}
                textAnchor="middle"
                fill="#fff"
                fontSize={8}
                fontWeight={700}
              >
                YOU
              </text>
            </>
          )}
        </g>
      );
    });
  }

  if (rootNodes.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        <p className="text-sm">No heritage data to visualize</p>
      </div>
    );
  }

  return (
    <svg
      ref={svgRef}
      width="100%"
      height="100%"
      style={{
        background: darkMode
          ? 'radial-gradient(ellipse at center, #1a1a2e 0%, #0f0f1a 100%)'
          : 'radial-gradient(ellipse at center, #faf8ff 0%, #f0edf7 100%)',
        cursor: isPanning ? 'grabbing' : 'grab',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
    >
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="6" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g transform={`translate(${transform.x + 40}, ${transform.y + 20}) scale(${transform.scale})`}>
        {renderEdges(layoutNodes)}
        {renderNodes()}
      </g>

      {/* Zoom hint */}
      <text
        x="12" y="98%"
        fill={darkMode ? '#555' : '#aaa'}
        fontSize={10}
      >
        Scroll to zoom · Drag to pan
      </text>
    </svg>
  );
};

export default LineageCanvas;
